import React, {
  useLayoutEffect,
  useRef,
  useContext,
  useEffect,
  useState,
} from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ThemeContext } from "../../../context/theme-context";
import am5locales_tr_TR from "@amcharts/amcharts5/locales/tr_TR";
import am5locales_en_US from "@amcharts/amcharts5/locales/en_US";
import moment from "moment";
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorWithReload from '../../../components/charts/ErrorWithReload';

function formatDate(date) {
  if (!date) return "0000 / 01";

  if (Number(date) > 1_000_000_000_000) {
    const d = new Date(Number(date));
    const year = d.getFullYear();
    const month = ("0" + (d.getMonth() + 1)).slice(-2);
    return `${year} / ${month}`;
  }

  const str = String(date);
  const year = str.slice(0, 4);
  const month = str.slice(4, 6) || "01";
  return `${year} / ${month}`;
}

export default function ScoreBarChart(props) { // props: {data, averageValue, lastValue, chartId, onReload}
  const seriesRef = useRef(null);
  const xAxisRef = useRef(null);
  const rootRef = useRef(null);

  const currentTheme = useContext(ThemeContext).theme;
  const [theme, setTheme] = useState("dark");
  const [chartData, setChartData] = useState([]);
  const [labelMode, setLabelMode] = useState("year");
  const [hasError, setHasError] = useState(false);
  const [symbolCode, setSymbolCode] = useState('');
  const [symbolName, setSymbolName] = useState('');

  // Get the language code from redux store
  const { i18n } = useTranslation();
  const profileBasicDetail = useSelector(state => state.profile.basicDetail);
  const languageCode = profileBasicDetail.languagecode || i18n.language || 'tr-TR';

  // Get the selected security from props or redux
  const selectedSecurity = useSelector((state) => state.security?.selectedSecurity);

  useEffect(() => {
    try {
      if (props.data && props.data.length) {
        const formatted = props.data
          .map((d) => {
            let ts;

            if (typeof d.date === "number" && d.date > 1e11) {
              ts = d.date;
            } else {
              ts = moment(String(d.date), "YYYYMMDD").valueOf();
            }
            return { date: ts, value: d.value };
          });

        const monthsPerYear = {};
        formatted.forEach((d) => {
          const y = new Date(d.date).getFullYear();
          monthsPerYear[y] = (monthsPerYear[y] || 0) + 1;
        });
        const hasMultiMonth = Object.values(monthsPerYear).some((c) => c > 1);
        setLabelMode(hasMultiMonth ? "year-month" : "year");

        setChartData(formatted);
        setHasError(false);

        // Set symbol code and name from props or redux
        if (props.selectedSecurity && props.selectedSecurity.code) {
          setSymbolCode(props.selectedSecurity.code);
          setSymbolName(props.selectedSecurity.name || '');
        } else if (selectedSecurity) {
          setSymbolCode(selectedSecurity.code || '');
          setSymbolName(selectedSecurity.name || '');
        }
      } else {
        setChartData([]);
        if (props.data && props.data.length === 0) {
          // Data array exists but is empty
          setHasError(true);
        }
      }
      setTheme(currentTheme);
    } catch (error) {
      console.error("Error processing chart data:", error);
      setHasError(true);
    }
  }, [props.data, currentTheme, props.selectedSecurity, selectedSecurity]);

  // 2) Create/dispose chart & set data
  useLayoutEffect(() => {
    if (chartData.length === 0) {
      return;
    }

    try {
      // Create root element
      const root = am5.Root.new(props.chartId ?? "chartdiv");
      rootRef.current = root;

      // Dynamically set locale based on language code
      if (languageCode === 'en-US') {
        root.locale = am5locales_en_US;
        console.log("Using English locale for chart");
      } else {
        // Default to Turkish
        root.locale = am5locales_tr_TR;
        console.log("Using Turkish locale for chart");
      }

      root.setThemes([am5themes_Animated.new(root)]);

      // Create a new XY Chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          pinchZoomX: false,
          layout: root.verticalLayout,
          paddingTop: 15,
          paddingBottom: 10,
          paddingLeft: 30,
          paddingRight: 10,
          numberFormatter: am5.NumberFormatter.new(root, {
            numericFormat: "#,###,###",
            bigNumberPrefixes: [
              { number: 1e3, suffix: "K" },
              { number: 1e6, suffix: "M" },
              { number: 1e9, suffix: "B" },
              { number: 1e12, suffix: "T" },
            ],
          }),
          // cursor: am5xy.XYCursor.new(root, {
          //   behavior: "none",
          // }),

        })
      );

      // const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      // cursor.lineY.set("visible", false);
      // ----------- Y-AXIS -----------
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {
            minGridDistance: 20,
            opposite: true,
          }),
        })
      );

      yAxis.get("renderer").labels.template.setAll({
        fontSize: 12,
        color: theme === "dark" ? am5.color("#fff") : am5.color("#231F20"),
        stroke: theme === "dark" ? am5.color("#626262") : am5.color("#E4E4E4"),
        oversizedBehavior: "wrap",
        fontFamily: "Roboto-Medium",
        fill: theme === "dark" ? am5.color("#fff") : am5.color("#231F20"),
      });

      // ----------- X-AXIS -----------
      const axisRendererX = am5xy.AxisRendererX.new(root, {
        minGridDistance: chartData.length > 12 ? 45 : 35,
      });
      axisRendererX.labels.template.setAll({
        rotation: 30,
        centerX: am5.p50,
        centerY: am5.p50,
        paddingTop: 6,
        fontSize: 12,
        fontFamily: "Roboto-Medium",
        fill: theme === "dark" ? am5.color("#fff") : am5.color("#231F20"),
      });

      axisRendererX.grid.template.setAll({ visible: false });

      const baseInt =
        labelMode === "year"
          ? { timeUnit: "year", count: 1 }
          : { timeUnit: "month", count: 1 };

      const xAxis = chart.xAxes.push(
        am5xy.GaplessDateAxis.new(root, {
          baseInterval: baseInt,
          renderer: axisRendererX,
          tooltip: am5.Tooltip.new(root, {
            labelText: "{valueX.formatDate('MMM yyyy')}",
          }),
          dateFormats: {
            month: "MMM yyyy",
            year: "yyyy",
          },
          periodChangeDateFormats: {
            month: "MMM yyyy",
            year: "yyyy",
          },
        })
      );
      if (labelMode === "year") {
        xAxis.set("gridIntervals", [{ timeUnit: "year", count: 1 }]);
        xAxis.set("dateFormats", { year: "yyyy" });
        xAxis.set("periodChangeDateFormats", { year: "yyyy" });
      } else {
        xAxis.set("gridIntervals", [{ timeUnit: "month", count: 3 }]);
        xAxis.set("dateFormats", { month: "yyyy/MM", year: "yyyy" });
        xAxis.set("periodChangeDateFormats", { month: "yyyy/MM", year: "yyyy" });
      }
      am5.array.each(xAxis.dataItems, (di, idx) => {
        if (idx === 0 || idx === xAxis.dataItems.length - 1) {
          di.get("label").setAll({ forceHidden: false, visible: true });
        }
      });
      xAxis.get("renderer").labels.template.setAll({
        rotation: 45,
        centerY: am5.percent(0),
        centerX: am5.percent(0),
        fontSize: 12,
        fill: theme === "dark" ? am5.color("#fff") : am5.color("#231F20"),
      });

      // ----------- SERIES -----------
      // Create custom tooltip
      var tooltipHTML = createTooltipHTML();

      let tooltipSeries = am5.Tooltip.new(root, {
        keepTargetHover: true,
        getFillFromSprite: true,
        getStrokeFromSprite: true,
        autoTextColor: true,
        getLabelFillFromSprite: true,
        fill: am5.color("#333"),
      });

      tooltipSeries.get("background").setAll({
        fill: am5.color("#333"),
        fillOpacity: 0,
        strokeOpacity: 0,
        getStrokeFromSprite: true,
      });
      tooltipSeries.label.set("fill", am5.color("#231f20"));

      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "Series",
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          valueXField: "date", // Tarih alanı
          tooltip: tooltipSeries,
          tooltipHTML: tooltipHTML,
        })
      );

      series.columns.template.setAll({
        cornerRadiusTL: 0,
        cornerRadiusTR: 0,
        cornerRadiusBL: 0,
        cornerRadiusBR: 0,
        strokeOpacity: 0,
        fill: am5.color("#418079"),
      });

      // Color negative bars red, positive bars green
      series.columns.template.adapters.add("fill", (fill, target) => {
        if (target.dataItem && target.dataItem.get("valueY") < 0) {
          return am5.color("#A81B05");
        }
        return am5.color("#418079");
      });

      // ----------- AVERAGE LINE -----------
      const average = props.averageValue ?? 0;

      const rangeDataItem = yAxis.makeDataItem({
        value: average,
        paddingLeft: 0,
      });
      const range = yAxis.createAxisRange(rangeDataItem);
      const label = range.get("label");

      label.setAll({
        fill: theme === "dark" ? am5.color("#231F20") : am5.color("#231F20"),
        text: `${average}`,
        dx: -15,
        background: am5.RoundedRectangle.new(root, {
          fill: theme === "dark" ? am5.color("#E0E0E0") : am5.color("#E0E0E0"),
        }),
        populateText: true,
      });

      label.adapters.add("x", function (x) {
        return 0;
      });

      chart.plotContainer.children.push(label);

      range.get("grid").setAll({
        visible: true,
        stroke: theme === "dark" ? am5.color("#E4E4E4") : am5.color("#231F20"),
        strokeWidth: 2,
        opacity: 1,
        dx: 20,
        strokeDasharray: [],
        strokeOpacity: 0.5,
      });

      const lastValueDataItem = yAxis.makeDataItem({
        value: props.lastValue,
        paddingLeft: 0,
      });
      const lastValueRange = yAxis.createAxisRange(lastValueDataItem);

      // 2) Label ayarları
      const lastValueLabel = lastValueRange.get("label");
      lastValueLabel.setAll({
        text: `${props.lastValue}`,
        fill: am5.color("#fff"),
        dx: 5,
        background: am5.RoundedRectangle.new(root, {
          fill: am5.color("#42A5F5"),
        }),
        populateText: true,
      });

      // X adaptörünü sıfırlarsak label eksen üzerine konumlanır
      lastValueLabel.adapters.add("x", (_x) => 0);

      // Label'ı plotContainer'a eklemezsek, label yAxis ile ilişkili kalır ve eksende görünür
      // chart.plotContainer.children.push(lastValueLabel);

      // 3) Grid (çizgi) görünümü
      lastValueRange.get("grid").setAll({
        visible: true,
        stroke: theme === "dark" ? am5.color("#E4E4E4") : am5.color("#231F20"),
        strokeWidth: 2,
        opacity: 1,
        strokeDasharray: [],
        strokeOpacity: 0,
      });

      // ----------- CURSOR -----------
      // Enable cursor for better tooltip experience
      const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
        behavior: "none"
      }));
      cursor.lineY.set("visible", false);

      // Hide Y axis tooltip/label on cursor
      cursor.lineY.setAll({
        visible: false
      });

      // Hide Y axis tooltip text
      chart.set("cursor", am5xy.XYCursor.new(root, {
        behavior: "none",
        xAxis: xAxis,
        yAxis: yAxis
      }));

      // Disable cursor tooltips explicitly
      xAxis.set("tooltip", am5.Tooltip.new(root, {
        forceHidden: true
      }));

      yAxis.set("tooltip", am5.Tooltip.new(root, {
        forceHidden: true
      }));

      yAxis.get("renderer").grid.template.setAll({
        strokeDasharray: [4, 4],
        stroke: theme === "dark" ? am5.color("#626262") : am5.color("#E4E4E4"),
        strokeOpacity: 1
      });

      // Keep references
      xAxisRef.current = xAxis;
      seriesRef.current = series;

      // Now set data for the X-Axis and Series
      xAxis.data.setAll(chartData);
      series.data.setAll(chartData);

      return () => {
        root.dispose();
      };
    } catch (error) {
      console.error("Error rendering chart:", error);
      setHasError(true);
    }
  }, [chartData, theme, props.averageValue, props.lastValue, languageCode]); // Added languageCode as dependency

  // Create tooltip HTML
  const createTooltipHTML = () => {
    const dateLabel = languageCode === 'en-US' ? 'Date' : 'Tarih';
    const priceLabel = languageCode === 'en-US' ? 'Price' : 'Fiyat';
    const averageLabel = languageCode === 'en-US' ? 'Average' : 'Ortalama';

    const priceColor = '#2FBC99';
    const volumeColor = theme === 'dark' ? '#e0e0e0' : '#757575';
    const backgroundColor = theme === 'dark' ? 'rgba(18, 18, 18, 0.3)' : 'rgba(255, 255, 255, 0.3)';
    const textColor = theme === 'dark' ? '#fff' : '#231f20';

    return `<div style="padding: 5px; background-color: ${backgroundColor}; color: ${textColor}; border-radius: 5px; min-width: 140px; border:.5px solid #b5b5b5; backdrop-filter: blur(4px);">
    <div style="display:block; text-align:left; margin-bottom:0px;">
      <strong>${symbolCode}  <span style="color: ${priceColor}"> - ${symbolName}</span></strong>
    </div>
    
    <div style="display:block; margin-bottom:0px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0px;">
        <div style="display:flex; align-items:center;">
          <span style="font-size:13px; font-family:Roboto-Medium; background-color: ${priceColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right:2px;"></span>
          <span style="font-size:13px; font-family:Roboto-Medium;">${dateLabel}:</span>
        </div>
        <div style="text-align:right;">
          <span style="font-size:13px; font-family:Roboto-Medium;">{date.formatDate('dd/MM/yyyy')}</span>
        </div>
      </div>
      
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0px;">
        <div style="display:flex; align-items:center;">
          <span style="font-size:13px; font-family:Roboto-Medium; background-color: ${priceColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right:2px;"></span>
          <span style="font-size:13px; font-family:Roboto-Medium;">${priceLabel}:</span>
        </div>
        <div style="text-align:right;">
          <span style="font-size:13px; font-family:Roboto-Medium;">{valueY} ${props.currencySymbol || '₺'}</span>
        </div>
      </div>
      
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <div style="display:flex; align-items:center;">
          <span style="font-size:13px; font-family:Roboto-Medium; background-color: ${volumeColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right:2px;"></span>
          <span style="font-size:13px; font-family:Roboto-Medium;">${averageLabel}:</span>
        </div>
        <div style="text-align:right;">
          <span style="font-size:13px; font-family:Roboto-Medium;">${props.averageValue || 0}</span>
        </div>
      </div>
    </div>
  </div>`;
  };



  // Handle reload functionality
  const handleReload = () => {
    if (props.onReload && typeof props.onReload === 'function') {
      setHasError(false); // Reset error state
      props.onReload();
    }
  };

  return (
    <>
      {hasError || !props.data || props.data.length === 0 ? (
        <ErrorWithReload
          onReload={handleReload}
          height="290px"
        />
      ) : (
        <div
          id={props.chartId ?? "chartdiv"}
          style={{ width: "100%", height: "290px" }}
        />
      )}
    </>
  );
}
